import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { IconClose } from './icons';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  title: string;
  message?: string;
  tone?: ToastTone;
  /** 0 keeps it until dismissed. Default 5000. */
  durationMs?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
}

export interface ToastApi {
  show: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast needs a <ToastProvider>.');
  return api;
}

function ToastView({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const { id, durationMs = 5000 } = toast;
  useEffect(() => {
    if (durationMs === 0) return;
    const timer = setTimeout(() => onDismiss(id), durationMs);
    return () => clearTimeout(timer);
  }, [id, durationMs, onDismiss]);
  return (
    <div className="eb-toast" data-tone={toast.tone ?? 'info'}>
      <div>
        <div className="eb-toast__title">{toast.title}</div>
        {toast.message ? <div className="eb-toast__msg">{toast.message}</div> : null}
      </div>
      <button
        type="button"
        className="eb-toast__x"
        aria-label="Dismiss"
        onClick={() => onDismiss(id)}
      >
        <IconClose size={16} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const show = useCallback((options: ToastOptions) => {
    const id = next.current++;
    setToasts((t) => [...t.slice(-4), { ...options, id }]);
    return id;
  }, []);
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="eb-toasts" role="region" aria-label="Notifications" aria-live="polite">
        {toasts.map((t) => (
          <ToastView key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
